import { APP_NAV, type AppNavItem } from './app-nav-config.js';
import { isNavItemActive } from './is-nav-item-active.js';
import { matchesRouteTemplate } from './matches-route-template.js';

export type AppBreadcrumb = Readonly<{
  label: string;
  to?: string;
  singular?: string;
}>;

function titleCaseSegment(segment: string): string {
  return segment
    .split('-')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function findNavMatch(pathname: string): { sectionTitle: string; item: AppNavItem } | null {
  let bestMatch: { sectionTitle: string; item: AppNavItem; length: number } | null = null;

  for (const section of APP_NAV) {
    for (const item of section.items) {
      if (!('to' in item) || !item.to) continue;
      if (!isNavItemActive(pathname, item.to)) continue;

      const length = item.to.length;
      if (!bestMatch || length > bestMatch.length) {
        bestMatch = { sectionTitle: section.title, item, length };
      }
    }
  }

  return bestMatch ? { sectionTitle: bestMatch.sectionTitle, item: bestMatch.item } : null;
}

const ID_SEGMENT_RE = /^\d+$|^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function segmentLabel(segment: string, labelsById: Record<string, string>): string {
  if (segment === 'new') return 'New';
  if (Object.hasOwn(labelsById, segment)) return labelsById[segment];
  if (ID_SEGMENT_RE.test(segment)) return 'Detail';
  return titleCaseSegment(segment);
}

function tailSegments(pathname: string, basePath: string): string[] {
  const remainder = pathname.slice(basePath.length).replace(/^\//, '');
  return remainder ? remainder.split('/').filter(Boolean) : [];
}

export function buildAppBreadcrumbs(
  pathname: string,
  labelsById: Record<string, string> = {},
  /**
   * Every route template the router can serve (`/warehouses/$locationGroupId`). A tail segment
   * matching none is a folder in the route tree rather than a page — `/inventory/counts` under
   * `/inventory/counts/new` — and linking it navigates to a Not Found. Left out, nothing is filtered.
   */
  routablePaths?: ReadonlySet<string>,
): AppBreadcrumb[] {
  if (pathname === '/') {
    return [];
  }

  const match = findNavMatch(pathname);
  if (!match) {
    const segments = pathname.split('/').filter(Boolean);
    const label = titleCaseSegment(segments.at(-1) ?? 'Page');
    return [{ label }];
  }

  const { sectionTitle, item } = match;
  const crumbs: AppBreadcrumb[] = [{ label: sectionTitle }];

  if (!('to' in item) || !item.to) {
    crumbs.push({ label: item.label });
    return crumbs;
  }

  const onListRoute = pathname === item.to;
  if (onListRoute) {
    crumbs.push({ label: item.label });
    return crumbs;
  }

  crumbs.push({ label: item.label, to: item.to });

  const segments = tailSegments(pathname, item.to);
  const tailLabel = item.tailCrumbLabels?.[segments.join('/')];
  if (tailLabel) {
    crumbs.push({ label: tailLabel });
    return crumbs;
  }
  let cumulativePath = item.to;
  segments.forEach((segment, index) => {
    cumulativePath = `${cumulativePath}/${segment}`;
    const isCurrentPage = index === segments.length - 1;
    const isDirectNewSegment = segment === 'new' && index === 0;
    const linkable =
      !isCurrentPage &&
      (routablePaths === undefined || matchesRouteTemplate(routablePaths, cumulativePath));
    crumbs.push({
      label: segmentLabel(segment, labelsById),
      ...(linkable ? { to: cumulativePath } : {}),
      ...(isDirectNewSegment && item.singular ? { singular: item.singular } : {}),
    });
  });

  return crumbs;
}
